import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FileText, FileSpreadsheet, Download, Loader2, FileBarChart } from 'lucide-react';
import { toast } from 'sonner';
import { API, isMgmt } from '../lib/ops';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const cv = { get: (p, params) => axios.get(`${API}/clearview${p}`, { params }).then(r => r.data) };
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const isNumish = (s) => /^[($]?-?[\d,]+\.?\d*%?\)?$/.test((s || '').trim()) && /\d/.test(s || '');

export default function ClearviewReports() {
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  const [meta, setMeta] = useState(null);
  const [key, setKey] = useState('store_mgr');
  const [storeCv, setStoreCv] = useState('0');
  const [start, setStart] = useState(daysAgo(7));
  const [end, setEnd] = useState(daysAgo(1));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { cv.get('/reports').then(setMeta).catch(e => toast.error(e.response?.data?.detail || 'Failed to load report list')); }, []);

  const run = useCallback(() => {
    setLoading(true);
    cv.get('/report', { key, start, end, store_cv_id: storeCv })
      .then(setReport).catch(e => toast.error(e.response?.data?.detail || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [key, start, end, storeCv]);
  useEffect(() => { if (meta) run(); }, [meta, run]);

  const download = async (fmt) => {
    try {
      const res = await axios.get(`${API}/clearview/report/export/${fmt}`, { params: { key, start, end, store_cv_id: storeCv }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `Elmwood_${key}_${start}_to_${end}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const rows = report?.rows || [];
  const matrix = report?.layout === 'matrix';

  return (
    <div className="space-y-5 max-w-6xl" data-testid="clearview-reports-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Reports · Clearview</p>
        <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Clearview Reports</h1>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-[#E5E1DA] rounded-2xl p-3">
        <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Report
          <select value={key} onChange={e => setKey(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal normal-case text-slate-800" data-testid="report-select">
            {(meta?.reports || []).map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        {mgmt && (
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Store
            <select value={storeCv} onChange={e => setStoreCv(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal normal-case text-slate-800" data-testid="report-store-select">
              {(meta?.stores || []).map(s => <option key={s.cv_id} value={s.cv_id}>{s.label}</option>)}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">From
          <input type="date" value={start} onChange={e => setStart(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal" data-testid="report-start" /></label>
        <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">To
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal" data-testid="report-end" /></label>
        <Button onClick={run} disabled={loading} className="bg-[#F26522] hover:bg-[#d9551a] text-white rounded-full h-9 px-5" data-testid="report-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run'}
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Button onClick={() => download('pdf')} variant="outline" size="sm" className="rounded-full" data-testid="report-export-pdf"><FileText className="w-4 h-4 mr-1" />PDF</Button>
          <Button onClick={() => download('xlsx')} variant="outline" size="sm" className="rounded-full" data-testid="report-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
          <Button onClick={() => download('csv')} variant="outline" size="sm" className="rounded-full" data-testid="report-export-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
        </div>
      </div>

      {/* Report table */}
      <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4 sm:p-5 overflow-x-auto" data-testid="report-table">
        {report && <p className="text-xs text-slate-500 mb-3 font-semibold">{report.label} · {report.store_label} · {start} → {end}</p>}
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Loading live from Clearview…</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-slate-400"><FileBarChart className="w-8 h-8 mb-2" /><p className="text-sm">No data for this selection.</p></div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className={matrix && ri === 0 ? 'bg-[#3E2418] text-white' : ri % 2 ? 'bg-slate-50/60' : ''} data-testid={`report-row-${ri}`}>
                  {r.map((c, ci) => {
                    const isHeader = matrix && ri === 0;
                    return (
                      <td key={ci} className={`px-2.5 py-1.5 whitespace-nowrap ${isHeader ? 'font-bold text-[11px] uppercase tracking-wider' : isNumish(c) ? 'text-right tabular-nums text-slate-700' : ci === 0 ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                        {c}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[11px] text-slate-400">Time Card Summary, Sales Variance and Variance reports require additional setup and are coming in a follow-up update.</p>
    </div>
  );
}
